// Conversation Analysis Item for InboxPage
// Shows conversation analysis summary in inbox

import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, TrendingUp, Target, Lightbulb, Tag } from 'lucide-react';
import { ConversationAnalysis } from '../../types/conversationAnalysis.types';
import { markAnalysisAsRead } from '../../services/conversationAnalysisService';

interface ConversationAnalysisItemProps {
  analysis: ConversationAnalysis;
  onRead?: () => void;
}

const ConversationAnalysisItem: React.FC<ConversationAnalysisItemProps> = ({ 
  analysis, 
  onRead 
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = async () => {
    setExpanded(!expanded);
    if (!analysis.isRead && !expanded) {
      await markAnalysisAsRead(analysis.id);
      onRead?.();
    }
  };

  const sentimentColors: Record<string, string> = {
    positive: 'bg-emerald-100 text-emerald-700',
    neutral: 'bg-gray-100 text-gray-700',
    negative: 'bg-red-100 text-red-700',
    mixed: 'bg-blue-100 text-blue-700'
  };

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${
      analysis.isRead ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-300'
    }`}>
      {/* Header */}
      <div
        onClick={handleExpand}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center space-x-3 flex-1">
          <FileText size={20} className="text-blue-500" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800">Gesprek Analyse</h4>
            <p className="text-sm text-gray-600">{analysis.phase} gesprek • {formatDate(analysis.createdAt)}</p>
          </div>
          {!analysis.isRead && (
            <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
              Nieuw
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 border-t border-gray-200 bg-white">
          {/* Summary */}
          <div className="mb-4">
            <h5 className="font-medium text-gray-800 mb-2">Samenvatting</h5>
            <p className="text-sm text-gray-700">{analysis.summary}</p>
          </div>

          {/* Sentiment */}
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${sentimentColors[analysis.sentiment]}`}>
              {getSentimentLabel(analysis.sentiment)}
            </span>
          </div>

          {/* Key Points */}
          {analysis.keyPoints && analysis.keyPoints.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Target size={16} className="text-orange-500" />
                <h5 className="font-medium text-gray-800">Belangrijkste punten</h5>
              </div>
              <ul className="space-y-1">
                {analysis.keyPoints.map((point, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start">
                    <span className="mr-2">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Items */}
          {analysis.actionItems && analysis.actionItems.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp size={16} className="text-emerald-500" />
                <h5 className="font-medium text-gray-800">Actiepunten</h5>
              </div>
              <ul className="space-y-1">
                {analysis.actionItems.map((item, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start">
                    <span className="mr-2">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Growth Points */}
          {analysis.growthPoints && analysis.growthPoints.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Lightbulb size={16} className="text-yellow-500" />
                <h5 className="font-medium text-gray-800">Groeipunten</h5>
              </div>
              <ul className="space-y-1">
                {analysis.growthPoints.map((point, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start">
                    <span className="mr-2">💡</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Topics */}
          {analysis.topics && analysis.topics.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Tag size={16} className="text-purple-500" />
                <h5 className="font-medium text-gray-800">Topics</h5>
              </div>
              <div className="flex flex-wrap gap-2">
                {analysis.topics.map((topic, idx) => (
                  <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Cost Info */}
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
            Kosten: {analysis.creditCost} credits • Model: {analysis.aiModel}
          </div>
        </div>
      )}
    </div>
  );
};

function getSentimentLabel(sentiment: string): string {
  const labels: Record<string, string> = {
    positive: 'Positief',
    neutral: 'Neutraal',
    negative: 'Negatief',
    mixed: 'Gemengd'
  };
  return labels[sentiment] || sentiment;
}

function formatDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 60) return `${diffMins} minuten geleden`;
  if (diffHours < 24) return `${diffHours} uur geleden`;
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  
  return date.toLocaleDateString('nl-NL');
}

export default ConversationAnalysisItem;
